/* ==========================================================================
   Verdelisse Home — visual interactions (design layer only).

   This file NEVER calls cart/checkout APIs, never submits or intercepts the
   product form, and never stores cart state. Add to cart, cart and checkout
   are Dawn's own code. On the product page it only:
   - formats prices that Dawn/Liquid already rendered, for display,
   - mirrors the enabled/disabled state of Dawn's own submit button,
   - shows/hides the sticky bar (whose button natively submits Dawn's form).
   ========================================================================== */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ---------- Toast (visual feedback only) ---------- */
  let toastEl, toastT;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'vh-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    clearTimeout(toastT);
    toastT = setTimeout(() => toastEl.classList.remove('show'), 2600);
  }

  /* ---------- Money formatting for display (uses the shop's own format) ---------- */
  function formatMoney(cents, format) {
    const fmt = format || '€{{amount_with_comma_separator}}';
    const value = Math.round(Number(cents) || 0);
    const withDelims = (n, decimals, thousands, decimal) => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? w + decimal + frac : w;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return withDelims(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
        case 'amount_with_space_separator': return withDelims(value, 2, ' ', ',');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  }

  /* ---------- Header: mobile menu, dropdowns, search bar ---------- */
  function initHeader() {
    const header = $('[data-vh-header]');
    if (!header) return;
    const nav = $('#vh-nav', header);
    const menuBtn = $('[data-vh-menu-toggle]', header);
    const searchbar = $('[data-vh-searchbar]', header);
    const searchBtn = $('[data-vh-search-toggle]', header);

    const closeSubs = () => $$('.vh-sub.open', header).forEach((sub) => {
      sub.classList.remove('open');
      $(`[aria-controls="${sub.id}"]`, header)?.setAttribute('aria-expanded', 'false');
    });

    $$('[data-vh-sub-toggle]', header).forEach((t) => t.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const sub = document.getElementById(t.getAttribute('aria-controls'));
      const open = sub.classList.toggle('open');
      t.setAttribute('aria-expanded', String(open));
    }));
    document.addEventListener('click', (e) => { if (!e.target.closest('.vh-has-sub')) closeSubs(); });

    menuBtn?.addEventListener('click', () => {
      const o = nav.classList.toggle('open');
      menuBtn.setAttribute('aria-expanded', String(o));
      menuBtn.setAttribute('aria-label', o ? 'Close menu' : 'Open menu');
    });

    const toggleSearch = (force) => {
      const o = searchbar.classList.toggle('open', force);
      searchBtn.setAttribute('aria-expanded', String(o));
      if (o) setTimeout(() => $('input[type="search"]', searchbar)?.focus(), 30);
    };
    searchBtn?.addEventListener('click', () => toggleSearch());
    $('[data-vh-search-close]', header)?.addEventListener('click', () => toggleSearch(false));
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (searchbar?.classList.contains('open')) toggleSearch(false);
      closeSubs();
    });
  }

  /* ---------- Product page ---------- */
  function initProduct(root) {
    const sectionId = root.dataset.section;
    const moneyFormat = (() => {
      try { return JSON.parse($('[data-vh-money-format]', root)?.textContent || 'null'); } catch (e) { return null; }
    })();
    const fmt = (c) => formatMoney(c, moneyFormat);
    const submit = () => document.getElementById(`ProductSubmitButton-${sectionId}`);
    const sticky = $('[data-vh-sticky]', root);

    const currentPlan = () => {
      const r = $('[data-vh-plans] input[name="quantity"]:checked', root);
      return r ? { n: Number(r.value) || 1, disc: Number(r.dataset.disc) || 0, name: r.dataset.name || '' } : { n: 1, disc: 0, name: '' };
    };
    const base = () => {
      const row = $('[data-vh-price-row]', root);
      return row ? { price: Number(row.dataset.price) || 0, compare: Number(row.dataset.compare) || 0, title: row.dataset.variantTitle || '' } : null;
    };

    /* "Add to cart — €89,00": visual label appended inside Dawn's button (Dawn only rewrites its first <span>) */
    function ensureCtaPrice() {
      const btn = submit();
      if (!btn || $('.vh-cta-price', btn)) return;
      const first = btn.querySelector('span');
      if (!first) return;
      const s = document.createElement('span');
      s.className = 'vh-cta-price';
      s.setAttribute('aria-hidden', 'true');
      s.innerHTML = '<span class="vh-cta-sep">—</span><span data-vh-cta-val></span>';
      first.after(s);
    }

    function render() {
      const b = base();
      if (!b) return;
      const pl = currentPlan();
      const full = Math.max(b.compare, b.price);
      const now = Math.round(b.price * pl.n * (100 - pl.disc) / 100);
      const was = full * pl.n;
      const pct = was > now ? Math.round((1 - now / was) * 100) : 0;

      const row = $('[data-vh-price-row]', root);
      $('[data-vh-now-val]', row).textContent = fmt(now);
      const forEl = $('[data-vh-for]', row);
      if (forEl) { forEl.hidden = pl.n <= 1; forEl.textContent = pl.n > 1 ? `for ${pl.n}` : ''; }
      const wasEl = $('[data-vh-was]', row), badge = $('[data-vh-badge]', row);
      if (wasEl) { wasEl.hidden = !pct; $('[data-vh-was-val]', wasEl).textContent = fmt(was); }
      if (badge) { badge.hidden = !pct; badge.textContent = `Save ${pct}%`; }

      // per-plan prices follow the selected variant
      $$('[data-vh-plans] .vh-plan', root).forEach((label) => {
        const input = $('input', label);
        const n = Number(input.value) || 1, d = Number(input.dataset.disc) || 0;
        const p = Math.round(b.price * n * (100 - d) / 100);
        $('[data-vh-plan-price]', label).textContent = fmt(p);
        const w = $('[data-vh-plan-was]', label);
        if (w) { w.textContent = fmt(full * n); w.parentElement.hidden = !(full * n > p); }
      });

      ensureCtaPrice();
      const cta = $('[data-vh-cta-val]', root);
      if (cta) cta.textContent = fmt(now);

      if (sticky) {
        $$('[data-vh-sb-price]', sticky).forEach((el) => (el.textContent = fmt(now)));
        const sbWas = $('[data-vh-sb-was]', sticky);
        if (sbWas) { sbWas.hidden = !pct; $('[data-vh-sb-was-val]', sbWas).textContent = fmt(was); }
        const plan = $('[data-vh-sb-plan]', sticky);
        if (plan && pl.name) plan.textContent = pl.name;
        const vt = $('[data-vh-sb-variant]', sticky);
        if (vt && b.title) vt.textContent = b.title;
      }
    }

    // plan radios are native form fields; we only re-render the display
    root.addEventListener('change', (e) => { if (e.target.matches('[data-vh-plans] input')) render(); });

    // Dawn swaps the *children* of #price-<section> after a variant change → re-apply the display.
    // Observe direct children only (no subtree): render() writes text deeper inside the block,
    // and observing the subtree would re-trigger render() forever (page freeze).
    const priceEl = document.getElementById(`price-${sectionId}`);
    let priceObs = null;
    if (priceEl) {
      priceObs = new MutationObserver(() => { render(); priceObs.takeRecords(); });
      priceObs.observe(priceEl, { childList: true });
    }

    // Mirror Dawn's submit button state on the sticky button (sold out / unavailable)
    function mirror() {
      const btn = submit(), sb = sticky && $('[data-vh-sb-add]', sticky);
      if (!btn || !sb) return;
      sb.disabled = btn.hasAttribute('disabled');
    }
    const btn = submit();
    if (btn) new MutationObserver(() => { mirror(); ensureCtaPrice(); }).observe(btn, { attributes: true, attributeFilter: ['disabled'], childList: true });

    render();
    mirror();

    // Sticky bar visibility (after the main CTA scrolls out of view)
    if (sticky && btn && 'IntersectionObserver' in window) {
      document.body.classList.add('has-sticky');
      new IntersectionObserver(([e]) => {
        const past = !e.isIntersecting && e.boundingClientRect.top < 0;
        sticky.classList.toggle('show', past);
        sticky.setAttribute('aria-hidden', String(!past));
        sticky.inert = !past;
        document.body.classList.toggle('sticky-on', past);
      }).observe(btn);
      $('[data-vh-sb-back]', sticky)?.addEventListener('click', () => {
        const target = $('variant-selects', root) || $('[data-vh-plans]', root) || btn;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // Desktop gallery arrows + counter (drive Dawn's own thumbnail buttons)
    const viewer = $('[id^="GalleryViewer"]', root);
    const thumbs = () => $$('[id^="GalleryThumbnails"] .thumbnail', root).filter((t) => t.offsetParent !== null);
    if (viewer && thumbs().length > 1) {
      const wrap = document.createElement('div');
      wrap.innerHTML =
        '<button type="button" class="vh-car-btn prev" aria-label="Previous image"><svg class="vh-ico" aria-hidden="true"><use href="#vh-i-left"/></svg></button>' +
        '<button type="button" class="vh-car-btn next" aria-label="Next image"><svg class="vh-ico" aria-hidden="true"><use href="#vh-i-right"/></svg></button>' +
        '<span class="vh-car-count" aria-live="polite"></span>';
      const count = wrap.querySelector('.vh-car-count');
      const activeIndex = () => Math.max(0, thumbs().findIndex((t) => t.hasAttribute('aria-current')));
      const update = () => { count.textContent = `${activeIndex() + 1} / ${thumbs().length}`; };
      wrap.querySelectorAll('.vh-car-btn').forEach((b) => b.addEventListener('click', () => {
        const list = thumbs(), step = b.classList.contains('next') ? 1 : -1;
        list[(activeIndex() + step + list.length) % list.length]?.click();
        setTimeout(update, 50);
      }));
      Array.from(wrap.childNodes).forEach((n) => viewer.appendChild(n));
      $('[id^="GalleryThumbnails"]', root)?.addEventListener('click', () => setTimeout(update, 50));
      update();
    }
  }

  /* ---------- Reviews (visual filter / sort / paging / lightbox) ---------- */
  function initReviews(sec) {
    const grid = $('[data-vh-grid]', sec);
    if (!grid) return;
    const items = $$('.vh-rv', grid);
    const per = Number(sec.dataset.perPage) || 8;
    const total = Number(grid.dataset.total) || items.length;
    const st = { filter: 'all', sort: 'recent', shown: per };
    const filterSel = $('[data-vh-filter]', sec), sortSel = $('[data-vh-sort]', sec);
    const moreBtn = $('[data-vh-rv-more]', sec), showing = $('[data-vh-showing]', sec), empty = $('[data-vh-empty]', sec);

    items.forEach((it) => {
      const q = $('[data-vh-q]', it);
      if ($('[data-vh-more]', it)) q.classList.add('clamp');
    });

    function render() {
      let list = items.filter((r) => st.filter === 'all' ? true : st.filter === 'photos' ? r.dataset.ph === 'true' : r.dataset.r === st.filter);
      const ord = (r) => Number(r.dataset.order), rt = (r) => Number(r.dataset.r), ph = (r) => (r.dataset.ph === 'true' ? 1 : 0);
      list.sort(st.sort === 'high' ? (a, b) => rt(b) - rt(a) || ord(a) - ord(b)
        : st.sort === 'low' ? (a, b) => rt(a) - rt(b) || ord(a) - ord(b)
        : st.sort === 'photos' ? (a, b) => ph(b) - ph(a) || ord(a) - ord(b)
        : (a, b) => ord(a) - ord(b));
      items.forEach((r) => (r.hidden = true));
      const vis = list.slice(0, st.shown);
      vis.forEach((r) => { r.hidden = false; grid.appendChild(r); });
      const t = st.filter === 'all' ? total : list.length;
      showing.textContent = list.length ? `Showing 1–${vis.length} of ${t} reviews` : '';
      empty.hidden = list.length > 0;
      moreBtn.hidden = vis.length >= list.length;
    }
    filterSel.addEventListener('change', () => { st.filter = filterSel.value; st.shown = per; render(); });
    sortSel.addEventListener('change', () => { st.sort = sortSel.value; render(); });
    moreBtn.addEventListener('click', () => { st.shown += per; render(); });
    $$('[data-vh-star]', sec).forEach((b) => b.addEventListener('click', () => {
      const v = b.dataset.vhStar;
      st.filter = [...filterSel.options].some((o) => o.value === v) ? v : 'all';
      filterSel.value = st.filter; st.shown = per; render();
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));

    const lb = $('[data-vh-lightbox]', sec);
    sec.addEventListener('click', (e) => {
      const m = e.target.closest('[data-vh-more]');
      if (m) {
        const q = $('[data-vh-q]', m.closest('.vh-rv'));
        const open = q.classList.toggle('clamp') === false;
        m.textContent = open ? 'Show less' : 'Read more';
        m.setAttribute('aria-expanded', String(open));
        return;
      }
      const ph = e.target.closest('[data-vh-photo]');
      if (ph && lb) {
        const idx = ph.dataset.vhPhoto;
        const rv = items.find((r) => r.dataset.order === idx);
        const src = $('img', ph);
        const img = $('img', lb);
        img.src = src?.currentSrc || src?.src || '';
        img.className = src?.className || '';
        $('p', lb).textContent = rv ? `“${$('h4', rv).textContent}” — ${$('.vh-rv__who b', rv).textContent}` : '';
        lb.showModal();
      }
    });

    // Write-a-review dialog: light validation, then Shopify's native contact form submit
    const dlg = $('[data-vh-review-form]', sec);
    $('[data-vh-write]', sec)?.addEventListener('click', () => dlg?.showModal());
    const form = dlg && $('form', dlg);
    form?.addEventListener('submit', (e) => {
      const fd = new FormData(form);
      const err = $('[data-vh-rf-err]', form);
      const email = String(fd.get('contact[email]') || '').trim();
      const missing = !fd.get('contact[Rating]') ? 'Choose a star rating.'
        : !String(fd.get('contact[name]') || '').trim() ? 'Enter your name.'
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Enter your email address so we can confirm your review.'
        : String(fd.get('contact[body]') || '').trim().length < 10 ? 'Write at least a sentence about the product.' : '';
      if (missing) { e.preventDefault(); err.textContent = missing; }
    });
    if ($('[data-vh-review-posted]', sec)) toast('Thanks! Your review will appear once it’s been checked.');
    else if (form && $('[data-vh-rf-err]', form)?.textContent.trim()) dlg.showModal();

    render();
  }

  /* Dialog close: backdrop click or close button */
  document.addEventListener('click', (e) => {
    if (e.target.matches('dialog.vh-modal')) e.target.close();
    const x = e.target.closest('[data-vh-dclose]');
    if (x) x.closest('dialog')?.close();
  });

  /* ---------- Newsletter: friendly validation before Shopify's native submit ---------- */
  function initNewsletter() {
    $$('.vh-news form').forEach((f) => f.addEventListener('submit', (e) => {
      const input = $('input[type="email"]', f);
      const msg = f.parentElement.querySelector('[data-vh-news-msg]');
      const v = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        e.preventDefault();
        msg.className = 'vh-news__msg err';
        msg.textContent = v ? 'Check the email address — it looks incomplete.' : 'Enter your email address to subscribe.';
        input.focus();
      }
    }));
  }

  function init() {
    initHeader();
    $$('.vh-pdp-section').forEach(initProduct);
    $$('[data-vh-reviews]').forEach(initReviews);
    initNewsletter();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-init a section when it's re-rendered
  document.addEventListener('shopify:section:load', (e) => {
    const t = e.target;
    if (t.querySelector('[data-vh-header]')) initHeader();
    t.querySelectorAll('.vh-pdp-section').forEach(initProduct);
    t.querySelectorAll('[data-vh-reviews]').forEach(initReviews);
  });
})();
